import { getCurrentMonthYear } from '../../../../helpers/utils/utils';
import type { DrawCountdown } from '../rewardsPageTypes';
import { EARN_HERO_PARTICLES, EARN_RING_CIRCUMFERENCE } from '../earnHeroConstants';
import {
  formatCompactCount,
  formatCountdownHms,
  getDaysInCurrentMonth,
  getRingStrokeDashoffset,
  pad2,
} from '../earnHeroUtils';

type EarnHeroProps = {
  tickets: number;
  points: number;
  countdown: DrawCountdown;
};

export const EarnHero = ({ tickets, points, countdown }: EarnHeroProps) => {
  const monthDays = getDaysInCurrentMonth();
  const ringOffset = getRingStrokeDashoffset(countdown.days, monthDays);
  const hms = formatCountdownHms(countdown);

  return (
    <section className='en-hero'>
      <div className='hero-glow-wm hero-glow-gold' aria-hidden='true' />
      {EARN_HERO_PARTICLES.map((particle) => (
        <div
          key={`${particle.top}-${particle.left}`}
          className={`sv-particle ${particle.tone}`}
          style={{
            top: particle.top,
            left: particle.left,
            width: particle.width,
            height: particle.height,
            animationDelay: particle.delay,
            animationDuration: particle.duration,
          }}
          aria-hidden='true'
        />
      ))}

      <div className='en-hero-grid'>
        <div className='en-hero-left'>
          <div className='en-ticket-stack-wrap'>
            <div
              className='en-ticket-stack'
              aria-label={`${tickets.toLocaleString('en-US')} tickets earned this month`}
            >
              <div className='en-ticket-card en-ticket-c1' aria-hidden='true' />
              <div className='en-ticket-card en-ticket-c2' aria-hidden='true' />
              <div className='en-ticket-card en-ticket-c3' aria-hidden='true' />
              <div className='en-ticket-card en-ticket-c4' aria-hidden='true' />
              <div className='en-ticket-card en-ticket-c5' aria-hidden='true' />
              <div className='en-ticket-card en-ticket-top'>
                <div className='en-ticket-stats'>
                  <div className='en-ticket-stat'>
                    <div className='en-ticket-lbl'>Tickets</div>
                    <div className='en-ticket-num'>{tickets.toLocaleString('en-US')}</div>
                  </div>
                  <div className='en-ticket-divider' aria-hidden='true' />
                  <div className='en-ticket-stat pts'>
                    <div className='en-ticket-lbl'>Points</div>
                    <div className='en-ticket-num'>{formatCompactCount(points)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='en-eye'>
            <span className='en-eye-rule' aria-hidden='true' />
            <span className='en-eye-lbl'>Earn & save</span>
          </div>
          <h1 className='en-title'>
            Earn tickets <span className='en-gold'>& points</span> by doing things you already do.
          </h1>
          <p className='en-sub'>Pay rent, open the app, refer members. Every action counts.</p>
          <div className='en-live-row'>
            <span className='en-live-dot' aria-hidden='true' />
            <span className='en-live-tag live'>Live</span>
            <span className='en-live-sep' aria-hidden='true'>
              ·
            </span>
            <span className='en-live-tag dim'>{getCurrentMonthYear()}</span>
          </div>
        </div>

        <div className='en-right'>
          <div className='en-cd-lbl'>Rent drop closes in</div>
          <div className='en-ring-wrap'>
            <svg
              className='en-ring'
              viewBox='0 0 160 160'
              width='160'
              height='160'
              aria-hidden='true'
            >
              <circle
                className='en-ring-track'
                cx='80'
                cy='80'
                r='68'
                fill='none'
                strokeWidth='8'
              />
              <circle
                className='en-ring-fill'
                cx='80'
                cy='80'
                r='68'
                fill='none'
                strokeWidth='8'
                strokeLinecap='round'
                transform='rotate(-90 80 80)'
                strokeDasharray={EARN_RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
              />
            </svg>
            <div className='en-ring-content'>
              <div className='en-ring-num'>{pad2(countdown.days)}</div>
              <div className='en-ring-lbl'>days </div>
            </div>
          </div>
          <div className='en-ring-sub' aria-live='polite'>
            <span className='en-ring-hms'>{hms.hours}</span>
            <span className='en-ring-unit'>h</span>
            <span className='en-ring-dot' aria-hidden='true'>
              ·
            </span>
            <span className='en-ring-hms'>{hms.minutes}</span>
            <span className='en-ring-unit'>m</span>
            <span className='en-ring-dot' aria-hidden='true'>
              ·
            </span>
            <span className='en-ring-hms en-ring-sec'>{hms.seconds}</span>
            <span className='en-ring-unit'>s</span>
          </div>
        </div>
      </div>
    </section>
  );
};
